import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const ActivityFeed = ({ activities, onMoveUp, onMoveDown, showMoveUp, showMoveDown }) => {
  const getActivityIcon = (type) => {
    switch (type) {
      case 'submission':
        return 'Send';
      case 'edit':
        return 'Edit';
      case 'publish':
        return 'Rocket';
      case 'share':
        return 'Share2';
      case 'duplicate':
        return 'Copy';
      default:
        return 'Activity';
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'submission':
        return 'text-success';
      case 'edit':
        return 'text-primary';
      case 'publish':
        return 'text-accent';
      case 'share':
        return 'text-secondary';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatTime = (date) => {
    const now = new Date();
    const activityDate = new Date(date);
    const diffMs = now - activityDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return activityDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getActivityLink = (activity) => {
    // Link submissions to the selected form inside the consolidated analytics workspace.
    if (activity?.type === 'submission' && activity?.formId) {
      return `/analytics?tab=forms&id=${activity?.formId}`;
    }
    if (activity?.formId) {
      return `/form-builder/${activity?.formId}`;
    }
    return '#';
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-3 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-heading font-semibold text-foreground">
            Recent Activity
          </h2>
          <div className="flex items-center gap-2">
            {showMoveUp && (
              <button
                onClick={onMoveUp}
                className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-smooth"
                title="Move section up"
              >
                <Icon name="ArrowUp" size={16} />
              </button>
            )}
            {showMoveDown && (
              <button
                onClick={onMoveDown}
                className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-smooth"
                title="Move section down"
              >
                <Icon name="ArrowDown" size={16} />
              </button>
            )}
            <button className="text-xs text-primary hover:text-primary/80 transition-smooth font-medium">
              View All
            </button>
          </div>
        </div>
      </div>
      <div className="divide-y divide-border">
        {activities?.map((activity) => (
          <Link
            key={activity?.id}
            to={getActivityLink(activity)}
            className="flex items-center gap-2 px-4 py-2 hover:bg-muted/50 transition-smooth w-full group"
          >
            <div className={`
              w-7 h-7 rounded-lg bg-muted flex items-center justify-center flex-shrink-0
              ${getActivityColor(activity?.type)}
            `}>
              <Icon 
                name={getActivityIcon(activity?.type)} 
                size={14} 
              />
            </div>

            <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <p className="text-sm text-foreground font-medium group-hover:text-primary transition-smooth">
                  {activity?.description}
                </p>
                {activity?.count && (
                  <span className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs font-medium flex-shrink-0">
                    {activity?.count}
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-2 text-xs text-muted-foreground flex-shrink-0">
                <span className="truncate max-w-[200px]">{activity?.formName}</span>
                <span>•</span>
                <span className="whitespace-nowrap">{formatTime(activity?.timestamp)}</span>
              </div>
            </div>

            <Icon 
              name="ChevronRight" 
              size={14} 
              className="text-muted-foreground group-hover:text-primary transition-smooth flex-shrink-0"
            />
          </Link>
        ))}
      </div>
    </div>
  );
};

export default ActivityFeed;